import { useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import LoadingWrapper from "components/Loading/LoadingWrapper";
import useAPI from "hooks/useAPI";
import Dialog from "@mui/material/Dialog";
import scss from "./ReclamationsDetails.module.scss";
import ProfilePage from "pages/Profile/ProfilePage";
import Button from "components/Buttons/Button/Button";
import Table from "../../../../components/Table/Table";
import TableScrollHint from "../../../../components/Table/TableScrollHint";
import PaginationUI from "components/UI/PaginationUI";
import { useTranslation } from "react-i18next";

const ReclamationsDetails = () => {
    const { t } = useTranslation();
    const api = useAPI();
    const navigate = useNavigate();
    const { id, reclamation_token } = useParams();
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const [currentCommentPage, setCurrentCommentPage] = useState(1);
    const [allComments, setAllComments] = useState([]);

    const {
        data: reclamationsItemsComments,
        isLoading: isLoadingComments,
    } = useQuery(
        ["reclamationsItemsComments", selectedRowId, currentCommentPage],
        () =>
            api.list(`/reclamations/items/comments/${selectedRowId}`, {
                page: currentCommentPage,
            }),
        {
            enabled: !!selectedRowId,
            onSuccess: (data) => {
                if (currentCommentPage === 1) {
                    setAllComments(data.items);
                } else {
                    setAllComments((prev) => [...prev, ...data.items]);
                }
            },
        }
    );

    const handleLoadMore = () => {
        setCurrentCommentPage((prev) => prev + 1);
    };

    const closeComments = () => {
        setShowDialog(false);
        setSelectedRowId(null);
        setAllComments([]);
        setCurrentCommentPage(1);
    };

    const { data: reclamationsItems, isLoading } = useQuery(["reclamationsItems", reclamation_token, currentPage], () =>
        api.list(`/reclamations/items/${reclamation_token}`, { limit: 12, page: currentPage })
    );

    const [showDialog, setShowDialog] = useState(false);

    let products = (reclamationsItems?.items ?? []).map((item) => ({
        code: item?.product_sku,
        name: item?.product_name,
        id_reclamations: item?.id_reclamations,
        fiscal_clip: item?.fiscal_clip,
        fiscal_clip_date: item?.fiscal_clip_date,
        product_name: item?.product_name,
        description: item?.description,
        responsive_type: (item?.responsive_type ?? []).map((type) => type?.name).join(", "),
        id: item?.id,
        id_product: item?.id_product,
    }));

    return (
        <ProfilePage>
            <div className={scss.header}>
                <button type="button" className={scss.backButton} onClick={() => navigate("/reklamacije")} aria-label={t("global-default-text-Back")}>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M15.5 10h-11m0 0 4.5-4.5M4.5 10l4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <h4 className={scss.pageTitle}>{t("reclamations-details-title")}: {id}</h4>
            </div>

            <LoadingWrapper isLoading={isLoading}>
                <div className={scss.wrapper}>
                    <div className={scss.itemsList}>
                        <h5 className={scss.sectionTitle}>{t("reclamations-details-subtitle")}</h5>
                        <div className={scss.tableWrap}>
                            <TableScrollHint />
                            <Table>
                                <thead>
                                    <tr>
                                        <th>{t("reclamations-details-table_th-name")}</th>
                                        <th>{t("reclamations-details-table_th-code")}</th>
                                        <th>{t("reclamations-details-table_th-description")}</th>
                                        <th>{t("reclamations-details-table_th-responsive_type")}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products?.map((item, index) => {
                                        return (
                                            <tr key={item.id ?? index}>
                                                <td>
                                                    {item.id_product ? (
                                                        <Link
                                                            to={`/katalog/artikal/${item.id_product}`}
                                                            className={scss.productLink}
                                                        >
                                                            {item.name}
                                                        </Link>
                                                    ) : (
                                                        item.name
                                                    )}
                                                </td>
                                                <td>
                                                    {item.id_product ? (
                                                        <Link
                                                            to={`/katalog/artikal/${item.id_product}`}
                                                            className={scss.productLink}
                                                        >
                                                            {item.code}
                                                        </Link>
                                                    ) : (
                                                        item.code
                                                    )}
                                                </td>
                                                <td>{item.description}</td>
                                                <td>{item.responsive_type}</td>
                                                <td className={scss.actionCell}>
                                                    <button
                                                        type="button"
                                                        className={scss.commentButton}
                                                        aria-label={t("reclamations-details-delete_dialog-title")}
                                                        title={t("reclamations-details-delete_dialog-title")}
                                                        onClick={() => {
                                                            setAllComments([]);
                                                            setCurrentCommentPage(1);
                                                            setSelectedRowId(item.id);
                                                            setShowDialog(true);
                                                        }}
                                                    >
                                                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                                            <path d="M5.5 3.5h9A2.5 2.5 0 0 1 17 6v6a2.5 2.5 0 0 1-2.5 2.5H9l-4 3v-3.1A2.5 2.5 0 0 1 3 12V6a2.5 2.5 0 0 1 2.5-2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                                            <path d="M6.5 7.5h7M6.5 10.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {products?.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className={scss.emptyCell}>
                                                {t("reclamations-details-message-no_data")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <PaginationUI
                            activePage={currentPage}
                            setPage={setCurrentPage}
                            numberOfDisplayed={reclamationsItems?.pagination?.items_per_page ?? 12}
                            numberOfProducts={reclamationsItems?.pagination.total_items ?? 0}
                            changePage={() => {}}
                            page1={currentPage}
                            setCurrentPage1={setCurrentPage}
                        />
                    </div>
                </div>
            </LoadingWrapper>

            <Dialog open={showDialog} onClose={closeComments} maxWidth="md" aria-labelledby="reclamation-comments-title" PaperProps={{ className: scss.modalPaper }}>
                <div className={scss.modalHeader}>
                    <h2 id="reclamation-comments-title">{t("reclamations-details-delete_dialog-title")}</h2>
                    <button type="button" className={scss.modalCloseIcon} onClick={closeComments} aria-label={t("reclamations-details-delete_dialog-button")}>
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="m5 5 10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                <div className={scss.modalContent}>
                    {isLoadingComments && allComments.length === 0 ? (
                        <LoadingWrapper isLoading={true} />
                    ) : allComments.length === 0 ? (
                        <div className={scss.emptyComments}>{t("reclamations-details-message-no_comment")}.</div>
                    ) : (
                        <div className={scss.commentsList}>
                            {allComments.map((comment, index) => (
                                <div className={scss.commentItem} key={index}>
                                    <span className={scss.createdAt}>{comment.user_first_name + " " + comment.user_last_name} / {comment.created_at}</span>
                                    <div className={scss.chatBox} dangerouslySetInnerHTML={{ __html: comment.comment }} />
                                </div>
                            ))}
                            {reclamationsItemsComments?.pagination?.total_pages > currentCommentPage && (
                                <div className={scss.loadMore}>
                                    <Button buttonStyleBasic={{ fontSize: "13px" }} label={"Prikaži jos"} onClick={handleLoadMore} variant="outlined" loading={isLoadingComments} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={scss.modalFooter}>
                    <button type="button" className={scss.modalCloseButton} onClick={closeComments}>{t("reclamations-details-delete_dialog-button")}</button>
                </div>
            </Dialog>
        </ProfilePage>
    );
};

export default ReclamationsDetails;
